
import { useEffect, useRef } from 'react';
import { useDraft } from '../../hooks/useDraft';
import { useAuth } from '../../store/AuthContext';
import { saveDraft } from '../../utils/draftStorage';
import { toPersian } from '../../utils/persianNumber';
import { getPasswordStrength, registrationPasswordError } from '../../utils/passwordStrength';
import { EmojiFieldWrap } from './EmojiPicker';
import { formatPhoneInputValue } from '../../utils/validators';
import { Tray, ChartBar } from '@phosphor-icons/react';


// ─── Spinner ─────────────────────────────────────────────────────────

export function Spinner({ size = 20, color = 'var(--blue-500)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      style={{ animation: 'spin 0.8s linear infinite' }}>
      <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2.5" strokeDasharray="31.4" strokeDashoffset="10" strokeLinecap="round" />
    </svg>
  );
}

export function TicketAiSubmitOverlay({ open, message }) {
  if (!open) return null;
  return (
    <div
      className="ticket-ai-overlay"
      role="alertdialog"
      aria-busy="true"
      aria-live="polite"
      aria-label={message}
    >
      <div className="ticket-ai-overlay__bg" aria-hidden="true" />
      <div className="ticket-ai-overlay__panel fade-in">
        <div className="ticket-ai-overlay__logo-wrap">
          <span className="ticket-ai-overlay__ring" aria-hidden="true" />
          <div className="ticket-ai-overlay__logo-box">
            <img src="/pejvak-mark.svg" alt="پژواک رایانه" className="ticket-ai-overlay__logo" />
          </div>
        </div>
        <p className="ticket-ai-overlay__brand">پژواک رایانه</p>
        <p className="ticket-ai-overlay__title">{message}</p>
        <div className="ticket-ai-overlay__dots" aria-hidden="true">
          <span /><span /><span />
        </div>
      </div>
    </div>
  );
}

// ─── Badge ────────────────────────────────────────────────────────────
const BADGE_STYLES = {
  blue:   { bg: '#D9E4F7', color: '#1B3068', border: '#B7C9EC' },
  green:  { bg: '#dcfce7', color: '#15803d', border: '#bbf7d0' },
  red:    { bg: '#fee2e2', color: '#b91c1c', border: '#fecaca' },
  amber:  { bg: '#fef3c7', color: '#b45309', border: '#fde68a' },
  purple: { bg: '#f3e8ff', color: '#6d28d9', border: '#e9d5ff' },
  gray:   { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' },
  orange: { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
};

export function Badge({ children, variant = 'gray', style: extra }) {
  const c = BADGE_STYLES[variant] || BADGE_STYLES.gray;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 20,
      fontSize: 11, fontWeight: 600,
      background: c.bg, color: c.color,
      border: `1px solid ${c.border}`,
      whiteSpace: 'nowrap',
      ...extra,
    }}>
      {children}
    </span>
  );
}

// ─── Avatar ───────────────────────────────────────────────────────────
export function Avatar({ name = '', src, size = 36, color = '#29AAE1' }) {
  const initials = name.slice(0, 2);
  return src
    ? <img src={src} alt={name} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover' }} />
    : (
      <div style={{
        width: size, height: size, borderRadius: '50%',
        background: color, display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', fontSize: size * 0.38, fontWeight: 700, flexShrink: 0,
      }}>
        {initials}
      </div>
    );
}

// ─── Card ─────────────────────────────────────────────────────────────
export function Card({ children, style: extra, className }) {
  return (
    <div className={className} style={{
      background: '#fff',
      border: '1px solid rgba(35,58,122,0.10)',
      borderRadius: 14,
      boxShadow: '0 4px 16px rgba(35,58,122,0.08)',
      padding: 24,
      ...extra,
    }}>
      {children}
    </div>
  );
}

// ─── Button ───────────────────────────────────────────────────────────
export function Btn({ children, variant = 'primary', size = 'md', loading, disabled, style: extra, ...props }) {
  const s = {
    primary: { background: 'linear-gradient(135deg,#29AAE1,#233A7A)', color: '#fff', border: 'none', shadow: '0 4px 14px rgba(35,58,122,0.35)' },
    secondary: { background: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', shadow: 'none' },
    danger: { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', shadow: 'none' },
    ghost: { background: 'transparent', color: '#475569', border: '1px solid #e2e8f0', shadow: 'none' },
    success: { background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', shadow: 'none' },
  }[variant] || {};

  const pad = size === 'sm' ? '6px 12px' : size === 'lg' ? '12px 24px' : '9px 18px';
  const fs = size === 'sm' ? 12 : size === 'lg' ? 15 : 13;

  // Disabled (but not merely loading) always reads as an inactive gray
  // button, regardless of variant - re-enabling it smoothly transitions
  // back to the variant's real color via the existing 'all 0.15s'.
  const disabledStyle = disabled && !loading
    ? { background: '#e2e8f0', color: '#94a3b8', border: 'none', boxShadow: 'none' }
    : {};

  return (
    <button disabled={disabled || loading} {...props} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: pad, borderRadius: 8, fontSize: fs, fontWeight: 600,
      cursor: disabled || loading ? 'not-allowed' : 'pointer',
      transition: 'all 0.15s', fontFamily: 'inherit',
      background: s.background, color: s.color,
      border: s.border || 'none',
      boxShadow: s.shadow,
      ...disabledStyle,
      ...extra,
    }}>
      {loading ? <Spinner size={14} color="currentColor" /> : null}
      {children}
    </button>
  );
}

// ─── Input ────────────────────────────────────────────────────────────
export function Input({ label, error, style: extra, wrapStyle, dir = 'auto', ...props }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, ...wrapStyle }}>
      {label && <label style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{label}</label>}
      <input dir={dir} {...props} style={{
        padding: '10px 14px', border: `1.5px solid ${error ? '#fca5a5' : '#e2e8f0'}`,
        borderRadius: 8, fontSize: 13, outline: 'none',
        background: '#fff', fontFamily: 'inherit',
        transition: 'border-color 0.15s', width: '100%',
        ...extra,
      }} />
      {error && <span style={{ fontSize: 11, color: '#dc2626' }}>{error}</span>}
    </div>
  );
}

/** Mobile field: English digits only, max 11 (09xxxxxxxxx). */
export function PhoneInput({ label, error, onChange, onPaste, style: extra, wrapStyle, dir = 'ltr', ...props }) {
  const handleChange = (e) => {
    const next = formatPhoneInputValue(e.target.value);
    onChange?.({ ...e, target: { ...e.target, value: next } });
  };
  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData?.getData('text') || '';
    const next = formatPhoneInputValue(text);
    onChange?.({ ...e, target: { ...e.target, value: next } });
    onPaste?.(e);
  };
  return (
    <Input
      label={label}
      error={error}
      dir={dir}
      type="tel"
      inputMode="numeric"
      autoComplete="tel"
      maxLength={11}
      onChange={handleChange}
      onPaste={handlePaste}
      style={extra}
      wrapStyle={wrapStyle}
      {...props}
    />
  );
}

// ─── PasswordField ────────────────────────────────────────────────────
// Password input with a show/hide toggle. `style` styles the <input>.
export function PasswordField({ label, error, value, onChange, placeholder, style: extra, labelStyle, autoComplete = 'new-password' }) {
  const [shown, setShown] = useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label && <label style={labelStyle || { fontSize: 13, fontWeight: 600, color: '#334155' }}>{label}</label>}
      <div style={{ position: 'relative' }}>
        <input
          type={shown ? 'text' : 'password'} value={value} onChange={onChange}
          placeholder={placeholder} autoComplete={autoComplete}
          style={{
            padding: '10px 14px 10px 42px', border: `1.5px solid ${error ? '#fca5a5' : '#e2e8f0'}`,
            borderRadius: 8, fontSize: 13, outline: 'none', background: '#fff',
            fontFamily: 'inherit', width: '100%', boxSizing: 'border-box', direction: 'ltr', textAlign: 'right',
            ...extra,
          }}
        />
        <button
          type="button" onClick={() => setShown((v) => !v)} tabIndex={-1}
          aria-label={shown ? 'پنهان کردن رمز عبور' : 'نمایش رمز عبور'}
          title={shown ? 'پنهان کردن رمز عبور' : 'نمایش رمز عبور'}
          style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 4, color: '#64748b' }}
        >{shown ? '🙈' : '👁️'}</button>
      </div>
      {error && <span style={{ fontSize: 11, color: '#dc2626' }}>{error}</span>}
    </div>
  );
}

// ─── Textarea ─────────────────────────────────────────────────────────
export function Textarea({ label, error, style: extra, wrapStyle, emoji = true, ...props }) {
  const ref = useRef(null);
  const ta = (
    <textarea ref={ref} {...props} style={{
      padding: '10px 14px',
      paddingBottom: emoji ? 44 : 10,
      border: `1.5px solid ${error ? '#fca5a5' : '#e2e8f0'}`,
      borderRadius: 8, fontSize: 13, outline: 'none',
      background: '#fff', fontFamily: 'inherit', direction: 'rtl',
      resize: 'vertical', minHeight: 100, width: '100%',
      boxSizing: 'border-box',
      ...extra,
    }} />
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, ...wrapStyle }}>
      {label && <label style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{label}</label>}
      {emoji
        ? (
          <EmojiFieldWrap
            inputRef={ref}
            value={props.value}
            onInsert={(next) => props.onChange?.({ target: { value: next } })}
          >
            {ta}
          </EmojiFieldWrap>
        )
        : ta}
      {error && <span style={{ fontSize: 11, color: '#dc2626' }}>{error}</span>}
    </div>
  );
}

// ─── Select ───────────────────────────────────────────────────────────
export function Select({ label, error, options = [], style: extra, wrapStyle, ...props }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, ...wrapStyle }}>
      {label && <label style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{label}</label>}
      <select {...props} style={{
        padding: '10px 14px', border: `1.5px solid ${error ? '#fca5a5' : '#e2e8f0'}`,
        borderRadius: 8, fontSize: 13, outline: 'none',
        background: '#fff', fontFamily: 'inherit', direction: 'rtl',
        cursor: 'pointer', width: '100%', ...extra,
      }}>
        {options.map(({ value, label: lbl }) => (
          <option key={value} value={value}>{lbl}</option>
        ))}
      </select>
      {error && <span style={{ fontSize: 11, color: '#dc2626' }}>{error}</span>}
    </div>
  );
}

// ─── Password Strength Meter ────────────────────────────────────────
export function PasswordStrengthMeter({ password, requireRegistrationPolicy = false }) {
  if (!password) return null;
  const { score, max, label, color } = getPasswordStrength(password);
  const policyErr = requireRegistrationPolicy ? registrationPasswordError(password) : '';
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: 'flex', gap: 4 }}>
        {Array.from({ length: max }).map((_, i) => (
          <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i < score ? color : '#e2e8f0', transition: 'background 0.15s' }} />
        ))}
      </div>
      <span style={{ fontSize: 11, color: policyErr ? '#ef4444' : color, fontWeight: 600 }}>{policyErr || label}</span>
      {requireRegistrationPolicy && !policyErr && (
        <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>حداقل ۸ کاراکتر، شامل حرف، عدد و کاراکتر خاص</div>
      )}
    </div>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────
export function Skeleton({ w = '100%', h = 18, radius = 6, style: extra }) {
  return (
    <div style={{
      width: w, height: h, borderRadius: radius,
      background: 'linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)',
      backgroundSize: '200% 100%',
      animation: 'shimmer 1.4s infinite',
      ...extra,
    }} />
  );
}

// ─── StatusBadge (ticket) ─────────────────────────────────────────────
export const TICKET_STATUS = {
  answered:    { label: 'پاسخ داده شده', variant: 'green' },
  closed:      { label: 'بسته شده',       variant: 'gray' },
  in_progress: { label: 'در حال بررسی',   variant: 'blue' },
  pending:     { label: 'در انتظار',       variant: 'amber' },
  new:         { label: 'جدید',           variant: 'purple' },
  unseen:      { label: 'دیده‌نشده',      variant: 'amber' },
  seen:        { label: 'دیده‌شده',       variant: 'green' },
  unresponsed: { label: 'بدون پاسخ',      variant: 'red' },
};

export function TicketStatusBadge({ status }) {
  const s = TICKET_STATUS[status] || { label: status, variant: 'gray' };
  return <Badge variant={s.variant}>{s.label}</Badge>;
}

// ─── Empty State ──────────────────────────────────────────────────────
export function EmptyState({ icon, title, subtitle }) {
  return (
    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center' }}>
        {icon || <Tray size={44} weight="regular" />}
      </div>
      {title && <div style={{ fontSize: 16, fontWeight: 600, color: '#64748b', marginBottom: 8 }}>{title}</div>}
      {subtitle && <div style={{ fontSize: 13 }}>{subtitle}</div>}
    </div>
  );
}

// ─── Stat Card ───────────────────────────────────────────────────────
export function StatCard({ label, value, color = '#29AAE1', suffix = '' }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid rgba(35,58,122,0.1)', padding: '18px 20px', textAlign: 'center' }}>
      <div style={{ fontSize: 26, fontWeight: 800, color }}>{value}{suffix}</div>
      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{label}</div>
    </div>
  );
}

// ─── Minimal inline-SVG bar chart (no chart library dependency) ──────
export function MiniBarChart({ data, valueKey = 'value', labelKey = 'label', color = '#29AAE1', height = 120 }) {
  if (!data || data.length === 0) return <EmptyState icon={<ChartBar size={44} weight="regular" />} title="داده‌ای برای نمایش نیست" />;
  const max = Math.max(...data.map(d => d[valueKey] || 0), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height, padding: '8px 0' }}>
      {data.map((d, i) => (
        <div key={i} title={toPersian(`${d[labelKey]}: ${d[valueKey]}`)} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <div style={{ width: '100%', maxWidth: 28, height: Math.max(((d[valueKey] || 0) / max) * (height - 24), 2), background: color, borderRadius: '4px 4px 0 0', transition: 'height 0.2s' }} />
          <span style={{ fontSize: 9, color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 36 }}>{toPersian(d[labelKey])}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Page Header ─────────────────────────────────────────────────────
export function PageHeader({ title, subtitle, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
      <div style={{ minWidth: 0 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', margin: 0 }}>{title}</h1>
        {subtitle && <p style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

// ─── Toast Container ──────────────────────────────────────────────────
export function ToastContainer({ toasts, onRemove }) {
  return (
    <div style={{ position: 'fixed', bottom: 24, left: 24, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {toasts.map((t) => (
        <div key={t.id} className="fade-in" style={{
          padding: '12px 16px', borderRadius: 10, fontSize: 13, fontWeight: 500,
          background: t.type === 'error' ? '#fef2f2' : t.type === 'success' ? '#f0fdf4' : t.type === 'warning' ? '#fffbeb' : '#EDF3FC',
          color: t.type === 'error' ? '#dc2626' : t.type === 'success' ? '#15803d' : t.type === 'warning' ? '#b45309' : '#1B3068',
          border: `1px solid ${t.type === 'error' ? '#fecaca' : t.type === 'success' ? '#bbf7d0' : t.type === 'warning' ? '#fde68a' : '#B7C9EC'}`,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          maxWidth: 320, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 8,
        }} onClick={() => onRemove(t.id)}>
          {t.type === 'error' ? '✕' : t.type === 'success' ? '✓' : t.type === 'warning' ? '⚠' : 'ℹ'} {t.msg}
        </div>
      ))}
    </div>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────
export function Modal({ open, onClose, title, children, width = 520, closable = true }) {
  if (!open) return null;
  const dismiss = closable ? onClose : undefined;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'pj-modal-title' : undefined}
      style={{
      position: 'fixed', inset: 0, zIndex: 1200,
      background: 'rgba(15,23,42,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
    }} onClick={dismiss}>
      <div className="fade-in" style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: width,
        maxHeight: '90vh', overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }} onClick={(e) => e.stopPropagation()}>
        {title && (
          <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span id="pj-modal-title" style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{title}</span>
            {closable ? (
              <button type="button" onClick={onClose} aria-label="بستن" style={{ cursor: 'pointer', background: 'none', border: 'none', fontSize: 20, color: '#94a3b8', lineHeight: 1, minWidth: 44, minHeight: 44 }}>✕</button>
            ) : null}
          </div>
        )}
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

// ─── Confirm Dialog ───────────────────────────────────────────────────
export function ConfirmDialog({ open, onClose, onConfirm, title, message, danger }) {
  return (
    <Modal open={open} onClose={onClose} title={title} width={400}>
      <p style={{ color: '#475569', fontSize: 14, marginBottom: 24 }}>{message}</p>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Btn variant="ghost" onClick={onClose}>انصراف</Btn>
        <Btn variant={danger ? 'danger' : 'primary'} onClick={() => { onConfirm(); onClose(); }}>تأیید</Btn>
      </div>
    </Modal>
  );
}

// ─── DraftTextarea ────────────────────────────────────────────────────
// Single source of truth = internal draft state (+ localStorage).
// Do NOT pass a live controlled `value` from the parent for ordinary typing
// — that raced with restore and wiped drafts (parent starts as '' and
// overwrote whatever loadDraft restored). Parents should:
//   - read text via onChange (for submit)
//   - push external text via `insertText` / `insertNonce` (AI insert, edit prefill)
//   - clear after submit via clearTrigger
// The unused `value` prop is accepted and IGNORED on purpose so older
// call sites that still pass value={replyText} cannot wipe the draft.
export function DraftTextarea({
  draftKey, label, placeholder, rows = 4, style: extra,
  onChange: extOnChange, clearTrigger, insertText, insertNonce,
  value: _ignoredControlledValue, emoji = true, onSubmit,
}) {
  const { user } = useAuth() || {};
  const [value, setValue, clearD, saved] = useDraft(draftKey, '', user?.username);
  const taRef = useRef(null);

  // Keep parent submit-state in sync with whatever is shown/restored.
  useEffect(() => {
    extOnChange?.(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, draftKey, user?.username]);

  // Explicit parent overwrite (AI "insert into reply", edit-mode prefill).
  // insertNonce must be a positive/truthy changing token — initial 0 must
  // NOT wipe a draft restored from localStorage on mount.
  useEffect(() => {
    if (!insertNonce) return;
    if (insertText == null) return;
    setValue(insertText);
    extOnChange?.(insertText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insertNonce]);

  useEffect(() => {
    if (clearTrigger) clearD();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearTrigger]);

  // Enter submits (when onSubmit given); Shift+Enter inserts a newline.
  const onKeyDown = onSubmit ? (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      onSubmit();
    }
  } : undefined;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label && <label style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{label}</label>}
      {emoji
        ? (
          <EmojiFieldWrap
            inputRef={taRef}
            value={value}
            onInsert={(next) => { setValue(next); extOnChange?.(next); }}
          >
            <textarea ref={taRef} value={value} rows={rows} placeholder={placeholder} onKeyDown={onKeyDown}
              onChange={(e) => { setValue(e.target.value); extOnChange?.(e.target.value); }}
              onBlur={() => {
                if (draftKey && value) saveDraft(draftKey, value, user?.username);
              }}
              style={{
                padding: '10px 14px', paddingBottom: 44,
                border: '1.5px solid #e2e8f0', borderRadius: 8,
                fontSize: 13, fontFamily: 'inherit', resize: 'vertical', direction: 'rtl',
                outline: 'none', width: '100%', boxSizing: 'border-box', ...extra,
              }} />
          </EmojiFieldWrap>
        )
        : (
          <textarea ref={taRef} value={value} rows={rows} placeholder={placeholder} onKeyDown={onKeyDown}
            onChange={(e) => { setValue(e.target.value); extOnChange?.(e.target.value); }}
            onBlur={() => {
              if (draftKey && value) saveDraft(draftKey, value, user?.username);
            }}
            style={{
              padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: 8,
              fontSize: 13, fontFamily: 'inherit', resize: 'vertical', direction: 'rtl',
              outline: 'none', width: '100%', ...extra,
            }} />
        )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        {saved
          ? <span style={{ fontSize: 11, color: '#16a34a' }}>✓ پیش‌نویس ذخیره شد</span>
          : <span style={{ fontSize: 11, color: '#94a3b8' }}>پیش‌نویس خودکار</span>
        }
        {value && <button type="button" onClick={clearD} style={{ fontSize: 11, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>پاک کردن</button>}
      </div>
    </div>
  );
}
